

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import type {
  BrandIssue, BrandIssueCategory, BrandIssueGroup, BrandCheckResult, BrandSpec,
} from "../../../types";
import { useBrandCheck } from "../hooks/useBrandCheck";
import { computeDefaultFontSizeRange } from "../services/brandChecker";
import { buildFixPalette, nearestPaletteColor, type NavigateOutcome } from "../services/brandFixer";
import { normalizeHex } from "../../../utils/colourUtils";
import styles from "./BrandCheckPanel.module.css";

// ─── Category / group metadata ─────────────────────────────────────────────

const CATEGORY_LABEL: Record<BrandIssueCategory, string> = {
  fillColor:          "Shape Fill color",
  fontColor:          "Font color",
  lineColor:          "Line / Border color",
  bulletColor:        "Bullet color",
  fontType:           "Replace Font Types",
  fontSize:           "Font Size",
  fontStyle:          "Font Styling",
  lineStyle:          "Line Style",
  shapeFormatting:    "Shape Formatting",
  brandingAsset:      "Branding Asset",
  offSlide:           "Out of Space Items",
  layoutDeviation:    "Layout Deviation",
  doubleWords:        "Double words",
  multipleSpaces:     "Multiple spaces",
  hiddenObject:       "Master Hidden Objects",
  emptyPlaceholder:   "Empty Placeholders",
  notesPresent:       "Notes",
  animationsPresent:  "Animations",
  transitionsPresent: "Transitions",
  commentsPresent:    "Comments",
  other:              "Other",
};

const CATEGORY_GROUP: Record<BrandIssueCategory, BrandIssueGroup> = {
  fillColor: "branding", fontColor: "branding", lineColor: "branding", bulletColor: "branding",
  fontType: "branding", fontSize: "branding", fontStyle: "branding", lineStyle: "branding",
  shapeFormatting: "branding", brandingAsset: "branding", other: "branding",
  offSlide: "alignment", layoutDeviation: "alignment",
  doubleWords: "text", multipleSpaces: "text",
  hiddenObject: "slideElements", emptyPlaceholder: "slideElements", notesPresent: "slideElements",
  animationsPresent: "slideElements", transitionsPresent: "slideElements", commentsPresent: "slideElements",
};

const GROUP_LABEL: Record<BrandIssueGroup, string> = {
  branding: "Branding", alignment: "Alignment", text: "Text", slideElements: "Slide Elements",
};

const GROUP_ORDER: BrandIssueGroup[] = ["branding", "alignment", "text", "slideElements"];

// Preferred category order within each group — matches the mockup's row order.
// Any category not listed here (dead/legacy categories the checker no longer
// produces) is appended after these, so nothing is ever silently dropped.
const GROUP_CATEGORY_ORDER: Record<BrandIssueGroup, BrandIssueCategory[]> = {
  branding: ["fillColor", "fontColor", "lineColor", "bulletColor", "fontType", "fontSize", "fontStyle"],
  alignment: ["offSlide"],
  text: ["doubleWords", "multipleSpaces"],
  slideElements: [
    "hiddenObject", "emptyPlaceholder", "notesPresent",
    "animationsPresent", "transitionsPresent", "commentsPresent",
  ],
};

// Comments/Empty-Placeholders are report-only: navigable but never
// auto-fixed, because there's no non-destructive form of "fixing" them —
// deleting the placeholder or a review comment IS the fix. emptyPlaceholder
// in particular used to be auto-fixable until its detection
// (containedType === null) turned out to also match ordinary text
// placeholders that DO have content, which meant "Auto-fix all" deleted live
// text across a whole deck — see brandFixer.ts before ever re-enabling it.
// Master-Hidden-Objects, Animations, and Transitions ARE auto-fixable (reveal
// / remove-effect-only) since neither deletes any content. Notes IS
// auto-fixable too — Fix/Fix All clears the notes text entirely, a
// deliberately destructive fix per explicit product decision (see
// brandFixer.ts's applyNotesFix).
const REPORT_ONLY_CATEGORIES = new Set<BrandIssueCategory>([
  "emptyPlaceholder", "commentsPresent",
]);

const FEATURE_DESCRIPTION = "Scans against the active presentation's theme, fonts, and layout.";

// ─── Scanning checklist ─────────────────────────────────────────────────────
//
// The underlying scan is a single per-slide pass that checks everything at
// once (see brandChecker.ts) — these five rows are a presentational mapping
// of overall scan progress onto named phases, not five separate full-deck
// passes. Bucket boundaries are cumulative fractions of slides scanned.

interface ScanPhase {
  id: string;
  label: string;
  upTo: number;
}

const SCAN_PHASES: ScanPhase[] = [
  { id: "colors", label: "Brand colours — fills, fonts, lines & bullets", upTo: 0.35 },
  { id: "fonts", label: "Fonts & type sizes", upTo: 0.55 },
  { id: "alignment", label: "Alignment & off-slide items", upTo: 0.7 },
  { id: "text", label: "Text quality — double words, spacing", upTo: 0.85 },
  { id: "slideElements", label: "Slide elements — placeholders, notes, animations, comments", upTo: 1 },
];

type PhaseStatus = "done" | "current" | "pending";

const PHASE_STATUS_ICON: Record<PhaseStatus, string> = {
  done: "✓",
  current: "●",
  pending: "○",
};

function computePhaseStatuses(current: number, total: number): { phase: ScanPhase; status: PhaseStatus }[] {
  const pct = total > 0 ? current / total : 0;
  let prevUpTo = 0;
  return SCAN_PHASES.map((phase) => {
    let status: PhaseStatus;
    if (pct >= phase.upTo) status = "done";
    else if (pct >= prevUpTo) status = "current";
    else status = "pending";
    prevUpTo = phase.upTo;
    return { phase, status };
  });
}

interface ScanningLoaderProps {
  readonly title: string;
  readonly subtitle: string;
  readonly current: number;
  readonly total: number;
}

/**
 * Shared big-spinner + checklist loader for both the extracting and scanning
 * phases — clicking "Run Brand Check" shows one continuous-looking loading
 * screen instead of a small inline spinner that switches to this larger UI
 * once the scan itself starts. During extraction current/total are both 0,
 * which computePhaseStatuses already renders sensibly (first phase "current",
 * the rest "pending") with no special-casing needed.
 */
function ScanningLoader({ title, subtitle, current, total }: ScanningLoaderProps) {
  const phaseStatuses = computePhaseStatuses(current, total);
  return (
    <div className={styles.panel}>
      <div className={styles.scanHeading}>
        <span className={styles.spinnerIcon} aria-hidden="true" />
        <div>
          <p className={styles.scanTitle}>{title}</p>
          <p className={styles.scanSubtitle}>{subtitle}</p>
        </div>
      </div>
      <ul className={styles.scanChecklist}>
        {phaseStatuses.map(({ phase, status }) => {
          const statusClassKey = `scanChecklistItem_${status}`;
          const statusClassName = styles[statusClassKey] ?? "";
          return (
            <li key={phase.id} className={`${styles.scanChecklistItem} ${statusClassName}`}>
              <span className={styles.scanChecklistIcon} aria-hidden="true">
                {PHASE_STATUS_ICON[status]}
              </span>
              <span>{phase.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CategoryRow {
  category: BrandIssueCategory;
  issues: BrandIssue[];
}

interface GroupSectionData {
  group: BrandIssueGroup;
  rows: CategoryRow[];
  totalCount: number;
}

// A category the checker produces that isn't in CATEGORY_GROUP (shouldn't
// happen, but must never silently vanish from the results UI) falls back to
// "branding" — same catch-all as the pre-existing "other" category.
function resolveGroup(category: BrandIssueCategory): BrandIssueGroup {
  return CATEGORY_GROUP[category] ?? "branding";
}

/** Only groups/categories that actually have issues are shown — matches the mockup (no zero-count rows). */
function buildGroupSections(issues: BrandIssue[]): GroupSectionData[] {
  return GROUP_ORDER.map((group) => {
    const preferredCats = GROUP_CATEGORY_ORDER[group];
    const preferredSet = new Set(preferredCats);
    const extraCats = [...new Set(
      issues
        .filter((i) => resolveGroup(i.category) === group && !preferredSet.has(i.category))
        .map((i) => i.category),
    )];

    const rows: CategoryRow[] = [...preferredCats, ...extraCats]
      .map((category) => ({ category, issues: issues.filter((i) => i.category === category) }))
      .filter((row) => row.issues.length > 0);

    const totalCount = rows.reduce((sum, r) => sum + r.issues.length, 0);
    return { group, rows, totalCount };
  }).filter((section) => section.rows.length > 0);
}

// ─── Component ────────────────────────────────────────────────────────────────

interface BrandCheckPanelProps {
  readonly onBack?: () => void;
}

export function BrandCheckPanel({ onBack }: Readonly<BrandCheckPanelProps>) {
  const {
    state, checkFromActive, recheck, recheckWithFontRange, reset,
    fixedIds, navigate, fix, fixAll,
  } = useBrandCheck();

  let body: ReactNode;

  // ── Idle ─────────────────────────────────────────────────────────────────
  // Brand Check only ever validates the active presentation against its own
  // extracted theme/master — there is no separate "upload a template" flow,
  // so opening the panel kicks off the scan immediately instead of waiting
  // on a "Run Brand Check" click.
  const startedRef = useRef(false);
  useEffect(() => {
    if (state.phase === "idle" && !startedRef.current) {
      startedRef.current = true;
      void checkFromActive();
    }
  }, [state.phase, checkFromActive]);

  if (state.phase === "idle") {
    body = (
      <ScanningLoader
        title="Extracting brand from active presentation…"
        subtitle="Reading colours, fonts and layout before scanning the deck."
        current={0}
        total={0}
      />
    );

  // ── Extracting ───────────────────────────────────────────────────────────
  // Uses the same big spinner + checklist loader as scanning (see ScanningLoader
  // below) so the auto-started scan shows one continuous-looking loading
  // screen rather than switching from a tiny spinner to a large checklist
  // once the scan itself starts.
  } else if (state.phase === "extracting") {
    body = (
      <ScanningLoader
        title={`Extracting brand from ${state.filename}…`}
        subtitle="Reading colours, fonts and layout before scanning the deck."
        current={0}
        total={0}
      />
    );

  // ── Scanning ─────────────────────────────────────────────────────────────
  } else if (state.phase === "scanning") {
    const { current, total } = state;
    body = (
      <ScanningLoader
        title="Scanning your deck against the brand…"
        subtitle={`Checking colours, fonts, alignment, text and slide elements across all ${total} slide${total !== 1 ? "s" : ""}.`}
        current={current}
        total={total}
      />
    );

  // ── Error ────────────────────────────────────────────────────────────────
  } else if (state.phase === "error") {
    const canRecheck = Boolean(state.brand);
    body = (
      <div className={styles.panel}>
        {canRecheck && (
          <div className={styles.headerRow}>
            <button type="button" className={styles.recheckBtn} onClick={() =>  recheck()}>
              Recheck
            </button>
          </div>
        )}
        <div className={`${styles.statusBox} ${styles.statusError}`}>
          <span className={styles.errorLabel}>Error:</span>
          {state.message}
        </div>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => {
            // reset() puts state back to "idle", but startedRef only guards the
            // initial mount — without clearing it here the auto-start effect
            // would never fire again, leaving the loading screen stuck forever.
            startedRef.current = false;
            reset();
          }}
        >
          Start Over
        </button>
      </div>
    );

  // ── Done ─────────────────────────────────────────────────────────────────
  } else {
    const { result, brand, filename } = state;
    body = (
      <ResultsView
        result={result}
        brand={brand}
        filename={filename}
        fixedIds={fixedIds}
        onRecheck={() =>  recheck()}
        onRecheckWithFontRange={(min, max) => recheckWithFontRange(min, max)}
        onNavigate={(issue) =>  navigate(issue)}
        onFix={(issue) =>  fix(issue)}
        onFixAll={(issues) =>  fixAll(issues)}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <ModuleHeader title="Brand Check" subtitle="Deckorate" onBack={onBack} />
      {body}
    </div>
  );
}

// ─── Results view ─────────────────────────────────────────────────────────────

interface ResultsViewProps {
  readonly result: BrandCheckResult;
  readonly brand: BrandSpec;
  readonly filename: string;
  readonly fixedIds: Set<string>;
  readonly onRecheck: () => void;
  readonly onRecheckWithFontRange: (min: number, max: number) => void;
  readonly onNavigate: (issue: BrandIssue) => Promise<NavigateOutcome>;
  readonly onFix: (issue: BrandIssue) => void;
  readonly onFixAll: (issues: BrandIssue[]) => void;
}

function ResultsView({
  result, brand, filename, fixedIds, onRecheck, onRecheckWithFontRange, onNavigate, onFix, onFixAll,
}: ResultsViewProps) {
  const [activeGroup, setActiveGroup] = useState<BrandIssueGroup | null>(null);
  const { issues, totalSlides, scannedAt } = result;
  const scanTime = new Date(scannedAt).toLocaleTimeString();

  const handleToggleGroup = useCallback((group: BrandIssueGroup) => {
    setActiveGroup((g) => (g === group ? null : group));
  }, []);

  const unfixedAutoFixable = issues.filter((i) => !REPORT_ONLY_CATEGORIES.has(i.category) && !fixedIds.has(i.id));
  const handleFixAllSafe = useCallback(() => onFixAll(unfixedAutoFixable), [onFixAll, unfixedAutoFixable]);

  // ── All clean ────────────────────────────────────────────────────────────
  if (issues.length === 0) {
    return (
      <div className={styles.panel}>
        <p className={styles.featureDescription}>{FEATURE_DESCRIPTION}</p>
        <div className={`${styles.statusBox} ${styles.statusSuccess}`}>
          <span className={styles.successIcon} aria-hidden="true">✓</span>
          <div>
            <span className={styles.successLabel}>All checks passed!</span>
            <p className={styles.successSub}>
              Scanned {totalSlides} slide{totalSlides !== 1 ? "s" : ""} — no brand violations found.
            </p>
          </div>
        </div>
        <button type="button" className={styles.recheckAllBtn} onClick={onRecheck}>
          ↺ Recheck the deck
        </button>
        <div className={styles.footer}>
          <span className={styles.scanTime}>{filename} · Last scanned at {scanTime}</span>
        </div>
      </div>
    );
  }

  const allSections = buildGroupSections(issues);
  const displayedSections = activeGroup ? allSections.filter((s) => s.group === activeGroup) : allSections;

  return (
    <div className={styles.panel}>
      <p className={styles.featureDescription}>{FEATURE_DESCRIPTION}</p>

      <StatCards autoFixableCount={unfixedAutoFixable.length} totalCount={issues.length} />

      <CategoryChips sections={allSections} activeGroup={activeGroup} onToggle={handleToggleGroup} />

      {unfixedAutoFixable.length > 0 && (
        <button type="button" className={styles.autoFixAllBtn} onClick={handleFixAllSafe}>
          ✦ Auto-fix all {unfixedAutoFixable.length} safe issues
        </button>
      )}

      <div className={styles.categorySections}>
        {displayedSections.map((section) => (
          <GroupSection
            key={section.group}
            group={section.group}
            rows={section.rows}
            totalCount={section.totalCount}
            fixedIds={fixedIds}
            brand={brand}
            onNavigate={onNavigate}
            onFix={onFix}
            onFixAll={onFixAll}
            onRecheckWithFontRange={onRecheckWithFontRange}
          />
        ))}
      </div>

      <button type="button" className={styles.recheckAllBtn} onClick={onRecheck}>
        ↺ Recheck the deck
      </button>

      <div className={styles.footer}>
        <span className={styles.scanTime}>{filename} · Last scanned at {scanTime}</span>
      </div>
    </div>
  );
}

// ─── Stat cards ───────────────────────────────────────────────────────────────

interface StatCardsProps {
  readonly autoFixableCount: number;
  readonly totalCount: number;
}

function StatCards({ autoFixableCount, totalCount }: StatCardsProps) {
  return (
    <div className={styles.statCards}>
      <div className={`${styles.statCard} ${styles.statCardFix}`}>
        <span className={styles.statCardCount}>{autoFixableCount}</span>
        <span className={styles.statCardLabel}>ready to auto-fix</span>
      </div>
      <div className={`${styles.statCard} ${styles.statCardIssues}`}>
        <span className={styles.statCardCount}>{totalCount}</span>
        <span className={styles.statCardLabel}>{totalCount === 1 ? "issue" : "issues"} found</span>
      </div>
    </div>
  );
}

// ─── Category chips ───────────────────────────────────────────────────────────

interface CategoryChipsProps {
  readonly sections: GroupSectionData[];
  readonly activeGroup: BrandIssueGroup | null;
  readonly onToggle: (group: BrandIssueGroup) => void;
}

function CategoryChips({ sections, activeGroup, onToggle }: CategoryChipsProps) {
  return (
    <div className={styles.categoryChips} role="tablist">
      {sections.map(({ group, totalCount }) => (
        <button
          key={group}
          type="button"
          role="tab"
          aria-selected={activeGroup === group}
          className={`${styles.categoryChip} ${activeGroup === group ? styles.categoryChipActive : ""}`}
          onClick={() => onToggle(group)}
        >
          {GROUP_LABEL[group]} <span className={styles.categoryChipCount}>{totalCount}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Group section (BRANDING / ALIGNMENT / TEXT / SLIDE ELEMENTS) ─────────────

interface GroupSectionProps {
  readonly group: BrandIssueGroup;
  readonly rows: CategoryRow[];
  readonly totalCount: number;
  readonly fixedIds: Set<string>;
  readonly brand: BrandSpec;
  readonly onNavigate: (issue: BrandIssue) => Promise<NavigateOutcome>;
  readonly onFix: (issue: BrandIssue) => void;
  readonly onFixAll: (issues: BrandIssue[]) => void;
  readonly onRecheckWithFontRange: (min: number, max: number) => void;
}

function GroupSection({
  group, rows, totalCount, fixedIds, brand, onNavigate, onFix, onFixAll, onRecheckWithFontRange,
}: GroupSectionProps) {
  return (
    <div className={styles.categorySection}>
      <div className={styles.categorySectionHeader}>
        <span className={styles.categorySectionLabel}>{GROUP_LABEL[group].toUpperCase()}</span>
        <span className={styles.categorySectionCount}>{totalCount} issue{totalCount !== 1 ? "s" : ""}</span>
      </div>
      {rows.map(({ category, issues }) => (
        <TypeRow
          key={category}
          category={category}
          issues={issues}
          fixedIds={fixedIds}
          brand={brand}
          onNavigate={onNavigate}
          onFix={onFix}
          onFixAll={onFixAll}
          onRecheckWithFontRange={onRecheckWithFontRange}
        />
      ))}
    </div>
  );
}

// ─── Type row (e.g. "Shape Fill color", "Font Size") ──────────────────────────

interface TypeRowProps {
  readonly category: BrandIssueCategory;
  readonly issues: BrandIssue[];
  readonly fixedIds: Set<string>;
  readonly brand: BrandSpec;
  readonly onNavigate: (issue: BrandIssue) => Promise<NavigateOutcome>;
  readonly onFix: (issue: BrandIssue) => void;
  readonly onFixAll: (issues: BrandIssue[]) => void;
  readonly onRecheckWithFontRange: (min: number, max: number) => void;
}

function TypeRow({
  category, issues, fixedIds, brand, onNavigate, onFix, onFixAll, onRecheckWithFontRange,
}: TypeRowProps) {
  const [expanded, setExpanded] = useState(false);
  const label = CATEGORY_LABEL[category] ?? String(category);
  const fixable = !REPORT_ONLY_CATEGORIES.has(category);
  const unfixed = issues.filter((i) => !fixedIds.has(i.id));

  return (
    <div className={styles.typeRow}>
      <div className={styles.typeRowHeader}>
        <button
          type="button"
          className={styles.typeRowLabel}
          onClick={() => setExpanded((v) => !v)}
        >
          {label}
        </button>
        <div className={styles.typeRowActions}>
          {fixable && unfixed.length > 0 && (
            <button
              type="button"
              className={styles.fixAllBtn}
              onClick={() => onFixAll(unfixed)}
              title={`Fix all ${unfixed.length} remaining ${label} issues`}
            >
              Fix All
            </button>
          )}
          <span className={styles.affectedBadge}>{issues.length} affected</span>
          <button
            type="button"
            className={`${styles.typeRowChevron} ${expanded ? styles.typeRowChevronOpen : ""}`}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          >
            ▶
          </button>
        </div>
      </div>

      {category === "fontSize" && (
        <FontSizeRangeInputs brand={brand} onRerun={onRecheckWithFontRange} />
      )}

      {expanded && (
        <IssuePager
          issues={issues}
          fixedIds={fixedIds}
          fixable={fixable}
          brand={brand}
          onNavigate={onNavigate}
          onFix={onFix}
          onFixAll={onFixAll}
        />
      )}
    </div>
  );
}

// ─── Font size range inputs ────────────────────────────────────────────────────

interface FontSizeRangeInputsProps {
  readonly brand: BrandSpec;
  readonly onRerun: (min: number, max: number) => void;
}

function FontSizeRangeInputs({ brand, onRerun }: FontSizeRangeInputsProps) {
  const defaultRange = computeDefaultFontSizeRange(brand);
  const [min, setMin] = useState<number>(defaultRange?.min ?? 10);
  const [max, setMax] = useState<number>(defaultRange?.max ?? 40);
  const isValid = Number.isFinite(min) && Number.isFinite(max) && min > 0 && max > 0 && min <= max;

  return (
    <div className={styles.fontSizeInputs}>
      <label className={styles.fontSizeLabel}>
        <span>Min</span>
        <input
          type="number"
          min={1}
          className={styles.fontSizeInput}
          value={min}
          onChange={(e) => setMin(e.target.valueAsNumber)}
          aria-label="Minimum font size"
        />
      </label>
      <label className={styles.fontSizeLabel}>
        <span>Max</span>
        <input
          type="number"
          min={1}
          className={styles.fontSizeInput}
          value={max}
          onChange={(e) => setMax(e.target.valueAsNumber)}
          aria-label="Maximum font size"
        />
      </label>
      <button
        type="button"
        className={styles.rerunBtn}
        onClick={() => onRerun(min, max)}
        disabled={!isValid}
        title={isValid ? "Re-scan using this font-size range" : "Enter a valid Min ≤ Max range"}
      >
        Re-run to apply
      </button>
    </div>
  );
}

// ─── Issue pager (step through a category's issues one at a time) ─────────────

// Categories whose fix snaps a colour to the nearest brand-palette colour
// (see brandFixer.ts's applyFillColorFix/applyFontColorFix/bulletColor route).
const NEAREST_COLOR_CATEGORIES = new Set<BrandIssueCategory>(["fillColor", "fontColor", "bulletColor"]);

/** The exact target colour/font/size a fix would apply, computed the same way the fixer computes it — so the preview never lies about what "Fix" will do. */
function computeSwapTarget(issue: BrandIssue, brand: BrandSpec, palette: string[]): string | null {
  if (issue.category === "lineColor") {
    // applyLineColorFix always sets the shape's line to brand.lineColor directly.
    const hex = normalizeHex(brand.lineColor);
    return hex ? `#${hex}` : null;
  }
  if (NEAREST_COLOR_CATEGORIES.has(issue.category) && issue.actual) {
    // bulletColor's `actual` may be a comma-joined list of multiple off-brand
    // colours on one shape — preview the first as representative.
    const firstActual = issue.actual.split(",")[0]?.trim() ?? issue.actual;
    return nearestPaletteColor(firstActual, palette);
  }
  if (issue.category === "fontType") {
    const target = brand.bodyTextFormat.fontName || brand.fonts.bodyFont;
    return target && !target.startsWith("+") ? target : null;
  }
  if (issue.category === "fontSize" && brand.bodyTextFormat.fontSize > 0) {
    return `${brand.bodyTextFormat.fontSize}pt`;
  }
  return null;
}

interface IssuePreviewProps {
  readonly issue: BrandIssue;
  readonly brand: BrandSpec;
  readonly palette: string[];
}

/** A before→after preview when the fix has a concrete target (colour swatch or font/size text); falls back to the issue description otherwise. */
function IssuePreview({ issue, brand, palette }: IssuePreviewProps) {
  const target = computeSwapTarget(issue, brand, palette);
  const isColor = issue.category === "lineColor" || NEAREST_COLOR_CATEGORIES.has(issue.category);

  if (target && isColor && issue.actual) {
    const firstActual = issue.actual.split(",")[0]?.trim() ?? issue.actual;
    return (
      <div className={styles.colorSwap}>
        <ColorSwatch hex={firstActual} /><span>{firstActual}</span>
        <span className={styles.swapArrow} aria-hidden="true">→</span>
        <ColorSwatch hex={target} /><span>{target}</span>
      </div>
    );
  }
  if (target && !isColor && issue.actual) {
    return (
      <div className={styles.fontSwap}>
        <span>{issue.actual}</span>
        <span className={styles.swapArrow} aria-hidden="true">→</span>
        <span>{target}</span>
      </div>
    );
  }
  return <p className={styles.issueDesc}>{issue.description}</p>;
}

interface IssuePagerProps {
  readonly issues: BrandIssue[];
  readonly fixedIds: Set<string>;
  readonly fixable: boolean;
  readonly brand: BrandSpec;
  readonly onNavigate: (issue: BrandIssue) => Promise<NavigateOutcome>;
  readonly onFix: (issue: BrandIssue) => void;
  readonly onFixAll: (issues: BrandIssue[]) => void;
}

const TOAST_DURATION_MS = 2200;

function IssuePager({ issues, fixedIds, fixable, brand, onNavigate, onFix, onFixAll }: IssuePagerProps) {
  const [index, setIndex] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const palette = useMemo(() => buildFixPalette(brand), [brand]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Issues in a category can shrink (fixes applied) without this component
  // remounting — never index past the end.
  const clampedIndex = Math.min(index, issues.length - 1);
  const issue = issues[clampedIndex];

  const handleNavigate = useCallback(async () => {
    if (!issue) return;
    const outcome = await onNavigate(issue);
    if (outcome === "none") return; // host couldn't navigate at all — nothing to confirm
    setToast(
      outcome === "shape"
        ? "Jumped to the shape — selected & highlighted in the deck"
        : "Jumped to the slide",
    );
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, [issue, onNavigate]);

  if (!issue) return null;

  const isFixed = fixedIds.has(issue.id);
  const unfixed = issues.filter((i) => !fixedIds.has(i.id));

  return (
    <div className={styles.issuePager}>
      <div className={styles.issuePagerHeader}>
        <span className={styles.issueSlide}>Slide {issue.slideNumber}</span>
        <span className={styles.issueSep}>·</span>
        <span className={styles.issueShape}>&quot;{issue.shapeName}&quot;</span>
        {isFixed && <span className={styles.fixedBadge}>Fixed</span>}
      </div>

      <IssuePreview issue={issue} brand={brand} palette={palette} />

      <div className={styles.issuePagerNav}>
        <button
          type="button"
          className={styles.pagerNavBtn}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={clampedIndex === 0}
          aria-label="Previous issue"
        >
          ‹
        </button>
        <span className={styles.pagerCounter}>{clampedIndex + 1}/{issues.length}</span>
        <button
          type="button"
          className={styles.pagerNavBtn}
          onClick={() => setIndex((i) => Math.min(issues.length - 1, i + 1))}
          disabled={clampedIndex === issues.length - 1}
          aria-label="Next issue"
        >
          ›
        </button>
        <button
          type="button"
          className={styles.pagerEyeBtn}
          onClick={handleNavigate}
          aria-label={`Go to Slide ${issue.slideNumber} — ${issue.shapeName}`}
          title="Go to this slide/shape"
        >
          👁
        </button>
      </div>

      {toast && (
        <output className={styles.pagerToast}>
          <span className={styles.pagerToastIcon} aria-hidden="true">✓</span>
          {toast}
        </output>
      )}

      {fixable && (
        <div className={styles.issuePagerActions}>
          {!isFixed && (
            <button type="button" className={styles.fixThisOneBtn} onClick={() => onFix(issue)}>
              Fix this one
            </button>
          )}
          <button
            type="button"
            className={styles.fixAllInlineBtn}
            onClick={() => onFixAll(unfixed)}
            disabled={unfixed.length === 0}
            title={`Fix all ${unfixed.length} remaining issues in this category`}
          >
            Fix All {unfixed.length}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Color swatch ─────────────────────────────────────────────────────────────

function ColorSwatch({ hex }: Readonly<{ hex: string }>) {
  if (!hex.startsWith("#") || (hex.length !== 4 && hex.length !== 7)) return null;
  return (
    <span
      className={styles.swatch}
      style={{ backgroundColor: hex }}
      aria-hidden="true"
    />
  );
}
